// API client for the S3-Parquet parcel pipeline (replaces the legacy
// GeoJSON import flow). Endpoints are scoped under /parcels/admin/* and
// the spatial query lives at /parcels/by-tenant/{id}.
//
// Uploads land directly in s3://{parcels_bucket}/all/ via a presigned
// PUT URL — see putChunkWithProgress for the bare-XHR helper that avoids
// having the global axios Authorization header leak into the S3 request.
//
// `relativePath` may be a bare filename (`chunk-001.parquet`) or a path
// under `all/` (`Region=Region_1/Ward=4/parcels_x.parquet`) for hive-
// partitioned uploads. The backend validates and rejects traversal.
import client from './client';

export const listChunks = () =>
  client.get('/parcels/admin/chunks').then((r) => r.data);

export const getChunkUploadUrl = (relativePath) =>
  client
    .post('/parcels/admin/chunks/upload-url', { filename: relativePath })
    .then((r) => r.data);

export const deleteChunk = (relativePath) =>
  // Encode each path segment separately — `encodeURIComponent` would
  // turn `/` into `%2F` and the FastAPI route wouldn't match.
  client
    .delete(
      `/parcels/admin/chunks/${relativePath
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`,
    )
    .then((r) => r.data);

// Wipes every Parquet object under `all/`. Use before re-uploading a
// freshly-rebuilt parcel dataset — the pipeline generates new UUIDs per
// run, so a re-upload alongside the old files would leave S3 holding
// both schemas and double-count parcels during reindex.
export const deleteAllChunks = () =>
  client.delete('/parcels/admin/chunks').then((r) => r.data);

export const triggerReindex = () =>
  client.post('/parcels/admin/reindex').then((r) => r.data);

export const listReindexRuns = (limit = 20) =>
  client.get('/parcels/admin/reindex-runs', { params: { limit } }).then((r) => r.data);

export const getReindexRun = (runJobId) =>
  client.get(`/parcels/admin/reindex-runs/${runJobId}`).then((r) => r.data);

// Direct XMLHttpRequest PUT to a presigned S3 URL. We avoid axios because
// its global request interceptor attaches `Authorization: Bearer <jwt>` to
// every call — and S3 rejects an upload that has both a SigV4 query-string
// signature AND a non-AWS Authorization header. XHR has no inherited state
// and still gives us upload progress events.
export const putChunkWithProgress = (url, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error('Network error during S3 PUT'));
    xhr.send(file);
  });
